import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { SignOut, ChartBar, Lightning as LightningIcon, Crown, Lock, Star, Calendar, CalendarBlank, CaretRight, Clock, Warning, CheckCircle, GearSix, Trophy, Globe, SoccerBall, TennisBall, Robot } from '@phosphor-icons/react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { LanguageSelector } from '../components/LanguageSelector';
import { toast } from 'sonner';
import { MatchAnalysisCard } from '../components/MatchAnalysisCard';
import { UpgradeModal } from '../components/UpgradeModal';
import { ManageSubscriptionModal } from '../components/ManageSubscriptionModal';

export default function Dashboard() {
  const { user, logout, checkAuth } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [matches, setMatches] = useState([]);
  const [matchAnalyses, setMatchAnalyses] = useState({});
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [targetOdds, setTargetOdds] = useState(3);
  const [riskLevel, setRiskLevel] = useState('medium');
  const [dateRange, setDateRange] = useState('today');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showCustomDates, setShowCustomDates] = useState(false);
  const [generatedSlip, setGeneratedSlip] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showManageSubModal, setShowManageSubModal] = useState(false);
  const [eligibleMatchesCount, setEligibleMatchesCount] = useState(0);
  const [selectedPeriodLabel, setSelectedPeriodLabel] = useState(t('dateToday'));
  const [topLeaguesOnly, setTopLeaguesOnly] = useState(true); // Default to top leagues
  const [sportMode, setSportMode] = useState('auto'); // auto, football, tennis

  // Get today and tomorrow dates for min/max
  const today = new Date().toISOString().split('T')[0];
  const maxDate = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  // Check for successful Stripe payment
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const success = searchParams.get('success');
    
    if (sessionId && success === 'true') {
      const checkStatus = async () => {
        try {
          const response = await api.get(`/stripe/checkout-status/${sessionId}`);
          if (response.data.payment_status === 'paid') {
            toast.success(`🎉 ${t('congratsVip')}`);
            await checkAuth();
            navigate('/dashboard', { replace: true });
          }
        } catch {
          // Payment check failed - user can retry
        }
      };
      checkStatus();
    }
  }, [searchParams, checkAuth, navigate, t]);

  const loadMatches = async () => {
    try {
      const response = await api.get(`/matches?top_leagues_only=${topLeaguesOnly}`);
      // Filter only eligible matches for display
      const eligibleMatches = response.data.filter(m => m.status === 'NS' || !m.status);
      setMatches(eligibleMatches);
      setEligibleMatchesCount(eligibleMatches.length);
      
      // Load analysis for each match
      const analyses = {};
      for (const match of response.data) {
        try {
          const analysisResponse = await api.get(`/matches/${match.match_id}/analysis`);
          analyses[match.match_id] = analysisResponse.data;
        } catch {
          // Analysis not available for this match
        }
      }
      setMatchAnalyses(analyses);
    } catch {
      toast.error('Erro ao carregar jogos');
    } finally {
      setLoading(false);
    }
  };

  // Load matches when topLeaguesOnly changes
  useEffect(() => {
    loadMatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topLeaguesOnly]);

  // Load matches when date range changes
  useEffect(() => {
    loadMatchesForRange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, customStartDate, customEndDate, topLeaguesOnly]);

  // Set default custom dates when switching to custom
  useEffect(() => {
    if (dateRange === 'custom' && !customStartDate) {
      setCustomStartDate(today);
      const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      setCustomEndDate(tomorrow);
    }
    setShowCustomDates(dateRange === 'custom');
  }, [dateRange, customStartDate, today]);

  const loadMatchesForRange = async () => {
    if (dateRange === 'custom' && (!customStartDate || !customEndDate)) {
      return; // Wait for custom dates to be set
    }
    
    try {
      let params = `date_range=${dateRange}&top_leagues_only=${topLeaguesOnly}`;
      if (dateRange === 'custom') {
        params += `&start_date=${customStartDate}&end_date=${customEndDate}`;
      }
      
      const response = await api.get(`/matches/range?${params}`);
      const data = response.data;
      
      // Update eligible count and period label
      setEligibleMatchesCount(data.total_matches || 0);
      setSelectedPeriodLabel(data.period_label || 'Hoje');
      
    } catch {
      // Failed to load matches for range
    }
  };

  const handleGenerateSlip = async () => {
    setGenerating(true);
    setGeneratedSlip(null);

    try {
      const requestBody = {
        target_odds: targetOdds,
        risk_level: riskLevel,
        date_range: dateRange,
        top_leagues_only: topLeaguesOnly,
        sport_mode: sportMode
      };

      // Add custom dates if using custom range
      if (dateRange === 'custom') {
        requestBody.start_date = customStartDate;
        requestBody.end_date = customEndDate;
      }

      const response = await api.post('/sharpslips/generate', requestBody);
      setGeneratedSlip(response.data);
      toast.success(t('slipGenerated'));
      // Refresh user data to update last generation time
      await checkAuth();
    } catch (error) {
      console.error('Error generating slip:', error);
      if (error.response?.status === 403) {
        // Free user limit reached
        toast.error(error.response?.data?.detail || t('slipError'));
        setShowUpgradeModal(true);
      } else if (error.response?.status === 400) {
        // No quality matches available
        toast.error(error.response?.data?.detail || t('noQualityMatches'));
      } else {
        toast.error(error.response?.data?.detail || t('slipError'));
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleUpgraded = async () => {
    await checkAuth();
    toast.success('Agora você tem acesso ilimitado!');
  };

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'low': return 'text-risk-low bg-risk-low/15';
      case 'medium': return 'text-risk-medium bg-risk-medium/15';
      case 'high': return 'text-risk-high bg-risk-high/15';
      default: return 'text-white/70 bg-white/10';
    }
  };

  const getRiskLabel = (risk) => {
    switch (risk) {
      case 'low': return t('low');
      case 'medium': return t('medium');
      case 'high': return t('high');
      default: return risk;
    }
  };

  const dateRangeOptions = [
    { value: 'today', label: t('dateToday'), icon: '📅' },
    { value: 'tomorrow', label: t('dateTomorrow'), icon: '📆' },
    { value: 'today_tomorrow', label: t('dateTodayTomorrow'), icon: '🗓️' },
    { value: 'next_3_days', label: t('dateNext3Days'), icon: '📊' },
    { value: 'custom', label: t('dateCustom'), icon: '⚙️' }
  ];

  const getDateRangeLabel = () => {
    const option = dateRangeOptions.find(o => o.value === dateRange);
    if (dateRange === 'custom' && customStartDate && customEndDate) {
      return `${customStartDate} a ${customEndDate}`;
    }
    return option?.label || t('dateToday');
  };

  return (
    <div className="min-h-screen bg-background grain-overlay">
      {/* Navbar */}
      <nav className="border-b border-white/5 backdrop-blur-md bg-background/80 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="SharpBetAI" className="h-12 w-auto" />
          </div>
          <div className="flex items-center gap-4">
            {user?.plan === 'vip' ? (
              <>
                <div className="flex items-center gap-2 px-4 py-2 bg-primary/20 border border-primary rounded-full">
                  <Crown weight="fill" className="text-primary text-lg" />
                  <span className="text-primary font-bold text-sm uppercase">VIP</span>
                </div>
                <button
                  onClick={() => setShowManageSubModal(true)}
                  className="text-white/70 hover:text-white text-sm font-medium transition-colors"
                >
                  {t('manage')}
                </button>
              </>
            ) : user?.plan === 'pro' ? (
              <>
                <div className="flex items-center gap-2 px-4 py-2 bg-secondary/20 border border-secondary rounded-full">
                  <Star weight="fill" className="text-secondary text-lg" />
                  <span className="text-secondary font-bold text-sm uppercase">PRO</span>
                </div>
                <button
                  onClick={() => setShowManageSubModal(true)}
                  className="text-white/70 hover:text-white text-sm font-medium transition-colors"
                >
                  {t('manage')}
                </button>
              </>
            ) : (
              <button
                onClick={() => setShowUpgradeModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-primary/10 hover:bg-primary/20 border border-primary/30 rounded-full transition-colors duration-150"
                data-testid="upgrade-button"
              >
                <Crown weight="fill" className="text-primary text-base" />
                <span className="text-primary font-bold text-sm">{t('upgrade')}</span>
              </button>
            )}
            <Link 
              to="/history" 
              className="flex items-center gap-2 px-4 py-2 text-white/70 hover:text-white transition-colors duration-150"
              data-testid="history-link"
            >
              <ChartBar weight="duotone" className="text-lg" />
              <span className="font-medium">{t('history')}</span>
            </Link>
            {user?.is_admin && (
              <Link 
                to="/admin" 
                className="flex items-center gap-2 px-4 py-2 text-primary/80 hover:text-primary transition-colors duration-150"
                data-testid="admin-link"
              >
                <GearSix weight="duotone" className="text-lg" />
                <span className="font-medium">{t('admin')}</span>
              </Link>
            )}
            <LanguageSelector />
            <div className="px-4 py-2 text-white/70 font-medium">{user?.name}</div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 text-white/70 hover:text-white transition-colors duration-150"
              data-testid="logout-button"
            >
              <SignOut weight="duotone" className="text-lg" />
              <span className="font-medium">{t('logout')}</span>
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        {/* Generator Section */}
        <div className="mb-12">
          <div className="glass-surface p-8 rounded-sm border-primary/20">
            <h2 className="text-2xl font-heading font-bold text-white mb-6 flex items-center gap-3">
              <LightningIcon weight="duotone" className="text-primary text-3xl" />
              {t('generateSharpSlipAI')}
            </h2>
            
            {/* Date Range Selector */}
            <div className="mb-8">
              <label className="block text-sm font-bold text-white/70 mb-4 uppercase tracking-wide flex items-center gap-2">
                <Calendar weight="duotone" className="text-lg" />
                {t('matchPeriod')}
              </label>
              <div className="flex flex-wrap gap-2 mb-4">
                {dateRangeOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setDateRange(option.value)}
                    className={`px-4 py-2.5 text-sm font-bold rounded-full transition-all duration-150 flex items-center gap-2 ${
                      dateRange === option.value
                        ? 'bg-secondary text-background shadow-lg'
                        : 'bg-white/5 text-white/60 hover:bg-white/10 border border-white/10'
                    }`}
                    data-testid={`date-range-${option.value}`}
                  >
                    <span>{option.icon}</span>
                    <span>{option.label}</span>
                  </button>
                ))}
              </div>
              
              {/* Custom Date Range Inputs */}
              {showCustomDates && (
                <div className="flex flex-wrap items-center gap-4 p-4 bg-white/5 rounded-sm border border-white/10">
                  <div className="flex items-center gap-2">
                    <label className="text-white/50 text-sm">De:</label>
                    <input
                      type="date"
                      value={customStartDate}
                      min={today}
                      max={maxDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      className="bg-white/10 border border-white/20 rounded-sm px-3 py-2 text-white text-sm focus:outline-none focus:border-secondary"
                      data-testid="custom-start-date"
                    />
                  </div>
                  <CaretRight weight="bold" className="text-white/30" />
                  <div className="flex items-center gap-2">
                    <label className="text-white/50 text-sm">Até:</label>
                    <input
                      type="date"
                      value={customEndDate}
                      min={customStartDate || today}
                      max={maxDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      className="bg-white/10 border border-white/20 rounded-sm px-3 py-2 text-white text-sm focus:outline-none focus:border-secondary"
                      data-testid="custom-end-date"
                    />
                  </div>
                </div>
              )}
              
              {/* Eligible Matches Counter */}
              <div className="mt-4 flex items-center gap-3 p-3 bg-white/5 rounded-sm border border-white/10">
                {eligibleMatchesCount > 0 ? (
                  <>
                    <CheckCircle weight="fill" className="text-risk-low text-xl" />
                    <span className="text-white/70 text-sm">
                      <span className="text-white font-bold">{eligibleMatchesCount}</span> jogos elegíveis para {selectedPeriodLabel}
                    </span>
                  </>
                ) : (
                  <>
                    <Warning weight="fill" className="text-risk-high text-xl" />
                    <span className="text-white/70 text-sm">
                      {t('noEligibleMatches')} <span className="text-white font-bold">{selectedPeriodLabel}</span>. 
                      {t('tryOtherPeriod')}
                    </span>
                  </>
                )}
              </div>
            </div>
            
            {/* Sport Mode Selector */}
            <div className="mb-8">
              <label className="block text-sm font-bold text-white/70 mb-4 uppercase tracking-wide flex items-center gap-2">
                <Robot weight="duotone" className="text-lg" />
                {t('sportMode')}
              </label>
              <div className="flex gap-3">
                <button
                  onClick={() => setSportMode('auto')}
                  className={`flex-1 px-4 py-3 rounded-sm transition-all duration-150 flex items-center justify-center gap-2 ${
                    sportMode === 'auto'
                      ? 'bg-gradient-to-r from-primary to-secondary text-background shadow-lg'
                      : 'bg-white/5 text-white/50 hover:bg-white/10 border border-white/10'
                  }`}
                  data-testid="sport-mode-auto"
                >
                  <Robot weight={sportMode === 'auto' ? "fill" : "duotone"} className="text-lg" />
                  <div className="text-left">
                    <div className="font-bold text-sm">{t('autoMode')}</div>
                    <div className={`text-xs ${sportMode === 'auto' ? 'text-background/70' : 'text-white/40'}`}>
                      {t('autoModeDesc')}
                    </div>
                  </div>
                </button>
                <button
                  onClick={() => setSportMode('football')}
                  className={`flex-1 px-4 py-3 rounded-sm transition-all duration-150 flex items-center justify-center gap-2 ${
                    sportMode === 'football'
                      ? 'bg-primary text-background shadow-lg'
                      : 'bg-white/5 text-white/50 hover:bg-white/10 border border-white/10'
                  }`}
                  data-testid="sport-mode-football"
                >
                  <SoccerBall weight={sportMode === 'football' ? "fill" : "duotone"} className="text-lg" />
                  <div className="font-bold text-sm">{t('footballOnly')}</div>
                </button>
                <button
                  onClick={() => {
                    setSportMode('tennis');
                    toast.info(t('tennisSoon'));
                  }}
                  className={`flex-1 px-4 py-3 rounded-sm transition-all duration-150 flex items-center justify-center gap-2 ${
                    sportMode === 'tennis'
                      ? 'bg-secondary text-background shadow-lg'
                      : 'bg-white/5 text-white/50 hover:bg-white/10 border border-white/10'
                  }`}
                  data-testid="sport-mode-tennis"
                >
                  <TennisBall weight={sportMode === 'tennis' ? "fill" : "duotone"} className="text-lg" />
                  <div className="font-bold text-sm">{t('tennisOnly')}</div>
                </button>
              </div>
            </div>
            
            {/* League Filter Toggle */}
            <div className="mb-8">
              <label className="block text-sm font-bold text-white/70 mb-4 uppercase tracking-wide flex items-center gap-2">
                <Trophy weight="duotone" className="text-lg" />
                {t('leagueFilter')}
              </label>
              <div className="flex gap-3">
                <button
                  onClick={() => setTopLeaguesOnly(true)}
                  className={`flex-1 px-4 py-3 rounded-sm transition-all duration-150 flex items-center justify-center gap-2 ${
                    topLeaguesOnly
                      ? 'bg-primary text-background shadow-lg'
                      : 'bg-white/5 text-white/50 hover:bg-white/10 border border-white/10'
                  }`}
                  data-testid="filter-top-leagues"
                >
                  <Trophy weight={topLeaguesOnly ? "fill" : "duotone"} className="text-lg" />
                  <div className="text-left">
                    <div className="font-bold text-sm">{t('topLeaguesOnly')}</div>
                    <div className={`text-xs ${topLeaguesOnly ? 'text-background/70' : 'text-white/40'}`}>
                      {t('qualityMatches')}
                    </div>
                  </div>
                </button>
                <button
                  onClick={() => setTopLeaguesOnly(false)}
                  className={`flex-1 px-4 py-3 rounded-sm transition-all duration-150 flex items-center justify-center gap-2 ${
                    !topLeaguesOnly
                      ? 'bg-secondary text-background shadow-lg'
                      : 'bg-white/5 text-white/50 hover:bg-white/10 border border-white/10'
                  }`}
                  data-testid="filter-all-leagues"
                >
                  <Globe weight={!topLeaguesOnly ? "fill" : "duotone"} className="text-lg" />
                  <div className="text-left">
                    <div className="font-bold text-sm">{t('allLeagues')}</div>
                    <div className={`text-xs ${!topLeaguesOnly ? 'text-background/70' : 'text-white/40'}`}>
                      {t('moreMatches')}
                    </div>
                  </div>
                </button>
              </div>
              {topLeaguesOnly && (
                <div className="mt-2 text-xs text-white/40 flex items-center gap-2">
                  <Trophy weight="fill" className="text-primary" />
                  {t('topLeaguesDescription')}
                </div>
              )}
            </div>
            
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              {/* Target Odds */}
              <div>
                <label className="block text-sm font-bold text-white/70 mb-4 uppercase tracking-wide">{t('targetOdds')}</label>
                <div className="grid grid-cols-4 gap-3">
                  {[2, 3, 4, 5].map((odds) => (
                    <button
                      key={odds}
                      onClick={() => setTargetOdds(odds)}
                      className={`px-4 py-3 font-mono text-xl font-bold rounded-sm transition-all duration-150 ${
                        targetOdds === odds
                          ? 'bg-primary text-background shadow-lg'
                          : 'bg-white/5 text-white/50 hover:bg-white/10 border border-white/10'
                      }`}
                      data-testid={`odds-button-${odds}`}
                    >
                      {odds}x
                    </button>
                  ))}
                </div>
              </div>

              {/* Risk Level */}
              <div>
                <label className="block text-sm font-bold text-white/70 mb-4 uppercase tracking-wide">{t('riskLevel')}</label>
                <div className="grid grid-cols-3 gap-3">
                  {['low', 'medium', 'high'].map((risk) => (
                    <button
                      key={risk}
                      onClick={() => setRiskLevel(risk)}
                      className={`px-4 py-3 text-sm font-bold rounded-sm transition-all duration-150 uppercase ${
                        riskLevel === risk
                          ? getRiskColor(risk) + ' shadow-lg'
                          : 'bg-white/5 text-white/50 hover:bg-white/10 border border-white/10'
                      }`}
                      data-testid={`risk-button-${risk}`}
                    >
                      {getRiskLabel(risk)}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <button
              onClick={handleGenerateSlip}
              disabled={generating}
              className="w-full px-8 py-4 bg-primary text-background font-black text-lg rounded-full hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 shadow-xl hover:shadow-primary/30 flex items-center justify-center gap-3"
              data-testid="generate-sharpslip-button"
            >
              {generating ? (
                <>
                  <div className="w-5 h-5 border-3 border-background border-t-transparent rounded-full animate-spin" />
                  {t('generating')}
                </>
              ) : (
                <>
                  <LightningIcon weight="fill" className="text-2xl" />
                  {t('generateSharpSlip')}
                </>
              )}
            </button>
          </div>
        </div>

        {/* Generated Slip Result */}
        {generatedSlip && (
          <div className="mb-12" data-testid="generated-slip-result">
            <div className="glass-surface p-8 rounded-sm border-secondary/30">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-heading font-bold text-white">{t('sharpSlipGenerated')}</h3>
                {generatedSlip.period_label && (
                  <div className="flex items-center gap-2 px-4 py-2 bg-secondary/20 border border-secondary/30 rounded-full">
                    <CalendarBlank weight="duotone" className="text-secondary" />
                    <span className="text-secondary font-bold text-sm">{generatedSlip.period_label}</span>
                  </div>
                )}
              </div>
              
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white/5 p-6 rounded-sm border border-white/10">
                  <div className="text-white/50 text-sm font-bold uppercase mb-2">{t('combinedOdds')}</div>
                  <div className="text-primary font-mono text-4xl font-bold" data-testid="combined-odds">{generatedSlip.combined_odds.toFixed(2)}x</div>
                </div>
                <div className="bg-white/5 p-6 rounded-sm border border-white/10">
                  <div className="text-white/50 text-sm font-bold uppercase mb-2">{t('probability')}</div>
                  <div className="text-secondary font-mono text-4xl font-bold" data-testid="probability">{generatedSlip.probability.toFixed(1)}%</div>
                </div>
                <div className="bg-white/5 p-6 rounded-sm border border-white/10">
                  <div className="text-white/50 text-sm font-bold uppercase mb-2">{t('confidence')}</div>
                  <div className="text-risk-low font-mono text-4xl font-bold" data-testid="confidence-score">{generatedSlip.confidence_score.toFixed(1)}%</div>
                </div>
              </div>

              <div className="mb-8">
                <h4 className="text-lg font-heading font-bold text-white mb-4">{t('selectedMatches')}</h4>
                <div className="space-y-3">
                  {generatedSlip.selected_matches.map((match, idx) => (
                    <div key={idx} className="bg-white/5 p-4 rounded-sm border border-white/10 flex items-center justify-between" data-testid={`selected-match-${idx}`}>
                      <div>
                        <div className="text-white font-semibold">{match.home_team} vs {match.away_team}</div>
                        <div className="text-white/50 text-sm">{match.market}: {match.selection}</div>
                      </div>
                      <div className="text-primary font-mono text-xl font-bold">{match.odds.toFixed(2)}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-secondary/10 border border-secondary/30 p-6 rounded-sm">
                <h4 className="text-lg font-heading font-bold text-white mb-3">{t('aiAnalysis')}</h4>
                <p className="text-white/70 leading-relaxed" data-testid="ai-reasoning">{generatedSlip.ai_reasoning}</p>
              </div>
            </div>
          </div>
        )}

        {/* Today's Matches */}
        <div>
          <h2 className="text-2xl font-heading font-bold text-white mb-6 flex items-center gap-3">
            {t('availableMatches')}
            <span className="text-sm font-normal text-white/50">
              ({matches.length} jogos)
            </span>
          </h2>
          
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : matches.length === 0 ? (
            <div className="glass-surface p-12 text-center rounded-sm">
              <Warning weight="duotone" className="text-5xl text-white/30 mx-auto mb-4" />
              <h3 className="text-xl font-heading font-bold text-white mb-2">Sem jogos disponíveis</h3>
              <p className="text-white/50">
                Não há jogos elegíveis para o período selecionado. 
                Os jogos podem já ter começado ou terminado.
              </p>
            </div>
          ) : (
            <div className="grid gap-6">
              {matches.map((match, idx) => (
                <div key={match.match_id} className="space-y-4">
                  <div className="glass-surface p-6 rounded-sm hover:border-white/20 transition-all duration-200" data-testid={`match-card-${idx}`}>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <div className="text-lg font-heading font-bold text-white">
                          {match.home_team} <span className="text-white/40">vs</span> {match.away_team}
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-white/40 text-sm">{match.league}</span>
                          <span className="text-white/20">•</span>
                          {match.match_date_formatted && (
                            <>
                              <span className="text-white/60 text-sm font-mono">{match.match_date_formatted}</span>
                              <span className="text-white/20">•</span>
                            </>
                          )}
                          <span className="text-white/60 text-sm font-mono flex items-center gap-1">
                            <Clock weight="bold" className="text-xs" />
                            {match.match_time}
                          </span>
                          {match.has_real_odds && (
                            <span className="px-2 py-0.5 bg-primary/20 text-primary text-xs font-bold rounded-full">LIVE ODDS</span>
                          )}
                          {match.status === 'NS' && (
                            <span className="px-2 py-0.5 bg-risk-low/20 text-risk-low text-xs font-bold rounded-full">ELEGÍVEL</span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Odds Grid - 2 rows */}
                    <div className="space-y-4">
                      {/* Row 1: 1X2, Over/Under 2.5, BTTS */}
                      <div className="grid grid-cols-3 gap-4">
                        {/* 1X2 */}
                        <div>
                          <div className="text-white/40 text-xs font-bold uppercase mb-2">1X2</div>
                          <div className="flex gap-2">
                            <div className="flex-1 bg-white/5 px-3 py-2 rounded-sm text-center">
                              <div className="text-white/40 text-xs">Casa</div>
                              <div className="text-white font-mono font-bold" data-testid={`match-${idx}-home-win`}>
                                {match.odds_1x2?.home?.toFixed(2) || '-'}
                              </div>
                            </div>
                            <div className="flex-1 bg-white/5 px-3 py-2 rounded-sm text-center">
                              <div className="text-white/40 text-xs">Empate</div>
                              <div className="text-white font-mono font-bold" data-testid={`match-${idx}-draw`}>
                                {match.odds_1x2?.draw?.toFixed(2) || '-'}
                              </div>
                            </div>
                            <div className="flex-1 bg-white/5 px-3 py-2 rounded-sm text-center">
                              <div className="text-white/40 text-xs">Fora</div>
                              <div className="text-white font-mono font-bold" data-testid={`match-${idx}-away-win`}>
                                {match.odds_1x2?.away?.toFixed(2) || '-'}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Over/Under 2.5 */}
                        <div>
                          <div className="text-white/40 text-xs font-bold uppercase mb-2">Over/Under 2.5</div>
                          <div className="flex gap-2">
                            <div className="flex-1 bg-white/5 px-3 py-2 rounded-sm text-center">
                              <div className="text-white/40 text-xs">Over</div>
                              <div className="text-white font-mono font-bold" data-testid={`match-${idx}-over-25`}>
                                {match.odds_over_under?.over_2_5?.toFixed(2) || '-'}
                              </div>
                            </div>
                            <div className="flex-1 bg-white/5 px-3 py-2 rounded-sm text-center">
                              <div className="text-white/40 text-xs">Under</div>
                              <div className="text-white font-mono font-bold" data-testid={`match-${idx}-under-25`}>
                                {match.odds_over_under?.under_2_5?.toFixed(2) || '-'}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* BTTS */}
                        <div>
                          <div className="text-white/40 text-xs font-bold uppercase mb-2">Ambas Marcam</div>
                          <div className="flex gap-2">
                            <div className="flex-1 bg-white/5 px-3 py-2 rounded-sm text-center">
                              <div className="text-white/40 text-xs">Sim</div>
                              <div className="text-white font-mono font-bold" data-testid={`match-${idx}-btts-yes`}>
                                {match.odds_both_teams_score?.yes?.toFixed(2) || '-'}
                              </div>
                            </div>
                            <div className="flex-1 bg-white/5 px-3 py-2 rounded-sm text-center">
                              <div className="text-white/40 text-xs">Não</div>
                              <div className="text-white font-mono font-bold" data-testid={`match-${idx}-btts-no`}>
                                {match.odds_both_teams_score?.no?.toFixed(2) || '-'}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Row 2: Double Chance, Over/Under 1.5 */}
                      <div className="grid grid-cols-2 gap-4">
                        {/* Double Chance */}
                        <div>
                          <div className="text-white/40 text-xs font-bold uppercase mb-2">Dupla Chance</div>
                          <div className="flex gap-2">
                            <div className="flex-1 bg-white/5 px-3 py-2 rounded-sm text-center">
                              <div className="text-white/40 text-xs">1X</div>
                              <div className="text-white font-mono font-bold" data-testid={`match-${idx}-dc-home-draw`}>
                                {match.odds_double_chance?.home_draw?.toFixed(2) || '-'}
                              </div>
                            </div>
                            <div className="flex-1 bg-white/5 px-3 py-2 rounded-sm text-center">
                              <div className="text-white/40 text-xs">X2</div>
                              <div className="text-white font-mono font-bold" data-testid={`match-${idx}-dc-away-draw`}>
                                {match.odds_double_chance?.away_draw?.toFixed(2) || '-'}
                              </div>
                            </div>
                            <div className="flex-1 bg-white/5 px-3 py-2 rounded-sm text-center">
                              <div className="text-white/40 text-xs">12</div>
                              <div className="text-white font-mono font-bold" data-testid={`match-${idx}-dc-home-away`}>
                                {match.odds_double_chance?.home_away?.toFixed(2) || '-'}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Over/Under 1.5 */}
                        <div>
                          <div className="text-white/40 text-xs font-bold uppercase mb-2">Over/Under 1.5</div>
                          <div className="flex gap-2">
                            <div className="flex-1 bg-white/5 px-3 py-2 rounded-sm text-center">
                              <div className="text-white/40 text-xs">Over</div>
                              <div className="text-white font-mono font-bold" data-testid={`match-${idx}-over-15`}>
                                {match.odds_over_under?.over_1_5?.toFixed(2) || '-'}
                              </div>
                            </div>
                            <div className="flex-1 bg-white/5 px-3 py-2 rounded-sm text-center">
                              <div className="text-white/40 text-xs">Under</div>
                              <div className="text-white font-mono font-bold" data-testid={`match-${idx}-under-15`}>
                                {match.odds_over_under?.under_1_5?.toFixed(2) || '-'}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Advanced Match Analysis */}
                  {matchAnalyses[match.match_id] && (
                    <MatchAnalysisCard 
                      match={match} 
                      analysis={matchAnalyses[match.match_id]}
                      userPlan={user?.plan || 'free'}
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Upgrade Modal */}
      <UpgradeModal 
        isOpen={showUpgradeModal} 
        onClose={() => setShowUpgradeModal(false)}
        onUpgraded={handleUpgraded}
      />

      {/* Manage Subscription Modal */}
      <ManageSubscriptionModal 
        isOpen={showManageSubModal}
        onClose={() => setShowManageSubModal(false)}
        user={user}
      />
    </div>
  );
}
